import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { hrApi } from '@/api';
import {
  BusinessUnitSelect,
  DepartmentSelect,
  GradeSelect,
  refreshOrgStructureCache,
} from '@/components/selects';
import { PageActionGate } from '@/components/auth/PageActionGate';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { FormGrid } from '@/components/ui/FormGrid';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import type { HrOrgStructure } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { cn } from '@/utils/cn';

export type OrgMasterTab = 'businessUnits' | 'departments' | 'designations' | 'grades';

export const ORG_MASTER_TABS: { id: OrgMasterTab; label: string; description: string }[] = [
  {
    id: 'businessUnits',
    label: 'Business units',
    description: 'Top-level divisions used to group departments and reporting',
  },
  {
    id: 'departments',
    label: 'Departments',
    description: 'Functional teams employees belong to — optional parent and business unit',
  },
  {
    id: 'grades',
    label: 'Grades',
    description: 'Job levels for compensation bands and designation mapping',
  },
  {
    id: 'designations',
    label: 'Designations',
    description: 'Job titles linked to a department and grade',
  },
];

export function isOrgMasterTab(tab: string): tab is OrgMasterTab {
  return ORG_MASTER_TABS.some((t) => t.id === tab);
}

type Props = {
  tab: OrgMasterTab;
  structure: HrOrgStructure | null;
  loading: boolean;
  createOpen: boolean;
  onCreateClose: () => void;
  onRefresh: () => Promise<void>;
};

function statusBadge(active: boolean) {
  return (
    <span
      className={cn(
        'rounded px-2 py-0.5 text-xs font-medium',
        active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-600',
      )}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
}

export function HrOrgMasterSection({ tab, structure, loading, createOpen, onCreateClose, onRefresh }: Props) {
  const businessUnitColumns: Column<HrOrgStructure['businessUnits'][number]>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'active', header: 'Status', render: (r) => statusBadge(r.active) },
  ];

  const departmentColumns: Column<HrOrgStructure['departments'][number]>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'active', header: 'Status', render: (r) => statusBadge(r.active) },
  ];

  const gradeColumns: Column<HrOrgStructure['grades'][number]>[] = [
    { key: 'code', header: 'Code' },
    { key: 'name', header: 'Name' },
    { key: 'levelOrder', header: 'Level' },
    { key: 'description', header: 'Description', render: (r) => r.description ?? '—' },
    { key: 'active', header: 'Status', render: (r) => statusBadge(r.active) },
  ];

  const designationColumns: Column<HrOrgStructure['designations'][number]>[] = useMemo(
    () => [
      { key: 'code', header: 'Code' },
      { key: 'name', header: 'Name' },
      {
        key: 'gradeId',
        header: 'Grade',
        render: (r) => {
          const grade = structure?.grades?.find((g) => g.id === r.gradeId);
          return grade ? `${grade.name} (${grade.code})` : r.grade ?? '—';
        },
      },
      { key: 'active', header: 'Status', render: (r) => statusBadge(r.active) },
    ],
    [structure?.grades],
  );

  const sortedGrades = useMemo(
    () => [...(structure?.grades ?? [])].sort((a, b) => a.levelOrder - b.levelOrder),
    [structure?.grades],
  );

  if (tab === 'businessUnits') {
    return (
      <>
        <DataTable
          columns={businessUnitColumns}
          data={structure?.businessUnits ?? []}
          loading={loading}
          keyField="id"
          emptyTitle="No business units yet — add your company divisions"
        />
        <OrgCreateModal tab={tab} open={createOpen} onClose={onCreateClose} onSaved={onRefresh} />
      </>
    );
  }

  if (tab === 'departments') {
    return (
      <>
        <DataTable
          columns={departmentColumns}
          data={structure?.departments ?? []}
          loading={loading}
          keyField="id"
          emptyTitle="No departments yet — create teams employees can be assigned to"
        />
        <OrgCreateModal tab={tab} open={createOpen} onClose={onCreateClose} onSaved={onRefresh} />
      </>
    );
  }

  if (tab === 'grades') {
    return (
      <>
        <DataTable
          columns={gradeColumns}
          data={sortedGrades}
          loading={loading}
          keyField="id"
          emptyTitle="No grades yet — define job levels (e.g. G1, G2, Senior)"
        />
        <OrgCreateModal tab={tab} open={createOpen} onClose={onCreateClose} onSaved={onRefresh} />
      </>
    );
  }

  return (
    <>
      <DataTable
        columns={designationColumns}
        data={structure?.designations ?? []}
        loading={loading}
        keyField="id"
        emptyTitle="No designations yet — add job titles and link them to grades"
      />
      <p className="text-sm text-slate-500">
        Set up{' '}
        <Link to="/hr/master-data?tab=grades" className="text-primary-600 hover:underline">
          grades
        </Link>{' '}
        before designations so titles inherit the correct level.
      </p>
      <OrgCreateModal tab={tab} open={createOpen} onClose={onCreateClose} onSaved={onRefresh} />
    </>
  );
}

function OrgCreateModal({
  tab,
  open,
  onClose,
  onSaved,
}: {
  tab: OrgMasterTab;
  open: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [levelOrder, setLevelOrder] = useState('1');
  const [description, setDescription] = useState('');
  const [parentDepartmentId, setParentDepartmentId] = useState('');
  const [businessUnitId, setBusinessUnitId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName('');
    setCode('');
    setLevelOrder('1');
    setDescription('');
    setParentDepartmentId('');
    setBusinessUnitId('');
    setDepartmentId('');
    setGradeId('');
  }, [open, tab]);

  const title =
    tab === 'businessUnits'
      ? 'Add business unit'
      : tab === 'departments'
        ? 'Add department'
        : tab === 'grades'
          ? 'Add grade'
          : 'Add designation';

  const save = async () => {
    if (!name.trim() || !code.trim()) {
      notify.error('Name and code are required');
      return;
    }
    setSaving(true);
    try {
      if (tab === 'businessUnits') {
        await hrApi.createBusinessUnit({ name: name.trim(), code: code.trim() });
        notify.success('Business unit created');
      } else if (tab === 'departments') {
        await hrApi.createDepartment({
          name: name.trim(),
          code: code.trim(),
          parentDepartmentId: parentDepartmentId ? Number(parentDepartmentId) : undefined,
          businessUnitId: businessUnitId ? Number(businessUnitId) : undefined,
        });
        notify.success('Department created');
      } else if (tab === 'grades') {
        await hrApi.createGrade({
          name: name.trim(),
          code: code.trim(),
          levelOrder: Number(levelOrder) || 0,
          description: description.trim() || undefined,
        });
        notify.success('Grade created');
      } else {
        await hrApi.createDesignation({
          name: name.trim(),
          code: code.trim(),
          parentDepartmentId: departmentId ? Number(departmentId) : undefined,
          gradeId: gradeId ? Number(gradeId) : undefined,
        });
        notify.success('Designation created');
      }
      const org = await hrApi.getOrgStructure();
      refreshOrgStructureCache(org);
      await onSaved();
      onClose();
    } catch (err) {
      showApiError(err, `Failed to create ${title.toLowerCase()}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <PageActionGate action="create">
            <Button loading={saving} onClick={() => void save()}>
              Save
            </Button>
          </PageActionGate>
        </>
      }
    >
      <FormGrid columns={2} className="gap-3">
        <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value)} required />
        {tab === 'grades' && (
          <>
            <Input
              label="Level order"
              type="number"
              min={0}
              value={levelOrder}
              onChange={(e) => setLevelOrder(e.target.value)}
            />
            <Input
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </>
        )}
        {tab === 'departments' && (
          <>
            <BusinessUnitSelect
              enabled={open}
              label="Business unit (optional)"
              placeholder="No business unit"
              value={businessUnitId}
              onChange={setBusinessUnitId}
            />
            <DepartmentSelect
              enabled={open}
              label="Parent department (optional)"
              placeholder="No parent"
              value={parentDepartmentId}
              onChange={setParentDepartmentId}
            />
          </>
        )}
        {tab === 'designations' && (
          <>
            <DepartmentSelect
              enabled={open}
              label="Department (optional)"
              placeholder="No department"
              value={departmentId}
              onChange={setDepartmentId}
            />
            <GradeSelect
              enabled={open}
              label="Grade (optional)"
              placeholder="No grade"
              value={gradeId}
              onChange={setGradeId}
            />
          </>
        )}
      </FormGrid>
    </Modal>
  );
}
